/**
 * Categorize documents based on content analysis
 */

export interface DocumentAnalysis {
  category: "research_paper" | "book_chapter" | "lecture_notes" | "article" | "thesis" | "report" | "other";
  confidence: number;
  indicators: string[];
  suggestedFormat: "APA" | "MLA" | "Chicago" | "CSE" | "Harvard";
  contentSummary: string;
}

interface CitationAnalysis {
  patterns?: string[];
  frequency?: Record<string, number>;
  authorYearCount?: number;
  numberedCount?: number;
}

export function categorizeDocument(
  content: string,
  fileName: string,
  citationAnalysis: CitationAnalysis
): DocumentAnalysis {
  const indicators: string[] = [];
  let researchScore = 0;
  let bookScore = 0;
  let lectureScore = 0;
  let articleScore = 0;
  let thesisScore = 0;
  let reportScore = 0;

  // Analyze file name
  const lowerFileName = fileName.toLowerCase();
  if (lowerFileName.includes("research")) researchScore += 20;
  if (lowerFileName.includes("paper")) researchScore += 15;
  if (lowerFileName.includes("chapter")) bookScore += 20;
  if (lowerFileName.includes("lecture") || lowerFileName.includes("notes")) lectureScore += 20;
  if (lowerFileName.includes("article")) articleScore += 20;
  if (lowerFileName.includes("thesis") || lowerFileName.includes("dissertation")) thesisScore += 25;
  if (lowerFileName.includes("report")) reportScore += 20;

  // Analyze content structure
  const contentLower = content.toLowerCase();
  const wordCount = content.split(/\s+/).filter(w => w.length > 0).length;

  // Check for abstract
  if (contentLower.includes("abstract")) {
    researchScore += 25;
    indicators.push("Contains abstract");
  }

  // Check for introduction, methodology, results, discussion, conclusion
  const hasIntro = contentLower.includes("introduction");
  const hasMethodology = contentLower.includes("methodology") || contentLower.includes("method");
  const hasResults = contentLower.includes("results") || contentLower.includes("findings");
  const hasDiscussion = contentLower.includes("discussion");
  const hasConclusion = contentLower.includes("conclusion");

  if (hasIntro && hasMethodology && hasResults && hasDiscussion && hasConclusion) {
    researchScore += 30;
    indicators.push("Contains research paper structure (Intro, Methodology, Results, Discussion, Conclusion)");
  } else if (hasIntro && hasConclusion) {
    researchScore += 15;
    indicators.push("Has introduction and conclusion sections");
  }

  // Check for references/bibliography
  if (
    contentLower.includes("references") ||
    contentLower.includes("bibliography") ||
    contentLower.includes("works cited")
  ) {
    researchScore += 20;
    thesisScore += 15;
    indicators.push("Contains references or bibliography");
  }

  // Check for learning objectives (lecture indicator)
  if (
    contentLower.includes("learning objective") ||
    contentLower.includes("learning outcome") ||
    contentLower.includes("key concepts")
  ) {
    lectureScore += 20;
    indicators.push("Contains learning objectives");
  }

  // Check for question sets (lecture/article indicator)
  const questionPattern = /\d+\.\s+[^.]*\?/g;
  const questionCount = (content.match(questionPattern) || []).length;
  if (questionCount > 3) {
    lectureScore += 15;
    articleScore += 10;
    indicators.push(`Contains ${questionCount} questions`);
  }

  // Check for headings (book chapter indicator)
  const headingCount = (content.match(/^#{1,3}\s+.+$/gm) || []).length;
  if (headingCount > 5) {
    bookScore += 15;
    indicators.push(`Contains ${headingCount} section headings`);
  }

  // Check for formal tone and academic language
  const academicWords = [
    "furthermore",
    "however",
    "therefore",
    "consequently",
    "demonstrate",
    "indicate",
    "suggest",
    "propose",
    "investigate",
    "analyze",
  ];
  let academicWordCount = 0;
  academicWords.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, "gi");
    const matches = content.match(regex);
    academicWordCount += matches ? matches.length : 0;
  });

  if (academicWordCount > 10) {
    researchScore += 15;
    thesisScore += 10;
    indicators.push("Uses academic language");
  }

  // Check for citations based on analysis
  const authorYearCount = citationAnalysis.authorYearCount || 0;
  const numberedCount = citationAnalysis.numberedCount || 0;

  if (authorYearCount > 5) {
    researchScore += 20;
    thesisScore += 15;
    indicators.push(`Contains ${authorYearCount} author-year citations`);
  }

  if (numberedCount > 5) {
    articleScore += 15;
    reportScore += 10;
    indicators.push(`Contains ${numberedCount} numbered citations`);
  }

  // Check word count
  if (wordCount > 10000) {
    thesisScore += 20;
    researchScore += 10;
    indicators.push("Long document (>10,000 words)");
  } else if (wordCount > 5000) {
    researchScore += 10;
    articleScore += 5;
    indicators.push("Medium-length document (5000-10000 words)");
  } else if (wordCount < 2000) {
    lectureScore += 10;
    articleScore += 10;
    indicators.push("Short document (<2000 words)");
  }

  // Check for charts, figures, tables
  const figurePattern = /figure|fig\.|table|table\.|chart|graph/gi;
  const figureCount = (content.match(figurePattern) || []).length;
  if (figureCount > 3) {
    researchScore += 10;
    reportScore += 10;
    indicators.push(`References ${figureCount} figures or tables`);
  }

  // Determine category based on scores
  const scores: Record<string, number> = {
    research_paper: researchScore,
    book_chapter: bookScore,
    lecture_notes: lectureScore,
    article: articleScore,
    thesis: thesisScore,
    report: reportScore,
  };

  let category: "research_paper" | "book_chapter" | "lecture_notes" | "article" | "thesis" | "report" | "other" = "other";
  let maxScore = 0;

  for (const [key, score] of Object.entries(scores)) {
    if (score > maxScore) {
      maxScore = score;
      category = key as "research_paper" | "book_chapter" | "lecture_notes" | "article" | "thesis" | "report";
    }
  }

  // If no clear winner, default to other
  if (maxScore < 20) {
    category = "other";
  }

  // Determine suggested format
  let suggestedFormat: "APA" | "MLA" | "Chicago" | "CSE" | "Harvard" = "APA";
  switch (category) {
    case "research_paper":
      suggestedFormat = "APA";
      break;
    case "thesis":
      suggestedFormat = "APA";
      break;
    case "book_chapter":
      suggestedFormat = "Chicago";
      break;
    case "article":
      suggestedFormat = "MLA";
      break;
    case "lecture_notes":
      suggestedFormat = "MLA";
      break;
    case "report":
      suggestedFormat = "CSE";
      break;
    default:
      suggestedFormat = "APA";
  }

  // Calculate confidence (0-100)
  const confidence = Math.min(100, Math.round((maxScore / 100) * 100));

  // Create content summary
  let contentSummary = `${wordCount} words, `;
  const sentences = content.split(/[.!?]+/).length;
  contentSummary += `${sentences} sentences, `;

  if (wordCount > 3000) {
    contentSummary += "lengthy document";
  } else if (wordCount > 1000) {
    contentSummary += "medium-length document";
  } else {
    contentSummary += "short document";
  }

  return {
    category,
    confidence,
    indicators,
    suggestedFormat,
    contentSummary,
  };
}

export function getDocumentTypeIcon(category: string): string {
  switch (category) {
    case "research_paper":
      return "📄";
    case "book_chapter":
      return "📖";
    case "lecture_notes":
      return "📚";
    case "article":
      return "📰";
    case "thesis":
      return "🎓";
    case "report":
      return "📊";
    default:
      return "📝";
  }
}
